import React, { useState, useEffect } from 'react';
import {
  Calendar as CalendarIcon,
  Search,
  Package,
  CheckCircle2,
  XCircle,
  Clock,
  Info,
  Briefcase,
  User
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { cn } from 'lib/utils';
import { getMyRequest } from 'services/creatorService';
import EquipmentRentDetailsModal from 'components/cpEquipments/EquipmentRentDetailsModal';

const cardHoverClass =
  "bg-white dark:bg-zinc-900 p-5 rounded-xl border border-[#E8DDD3] dark:border-zinc-800 shadow-sm " +
  "transition-all duration-300 ease-in-out " +
  "hover:border-[#e8d0aa] dark:hover:border-golden/50 hover:shadow-[4px_4px_0px_0px_#e8d0aa] dark:hover:shadow-[4px_4px_0px_0px_rgba(212,175,55,0.2)] " +
  "hover:scale-[1.01] cursor-pointer relative overflow-hidden flex flex-col";

const EquipmentRequestsPage = () => {
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    setIsLoading(true);
    try {
      const response = await getMyRequest();
      if (response && response.data) {
        setRequests(response.data);
      }
    } catch (error) {
      console.error("Error fetching requests:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case 1:
        return { label: "Confirmed", color: "bg-emerald-400", icon: CheckCircle2 };
      case 2:
        return { label: "Declined", color: "bg-red-400", icon: XCircle };
      default:
        return { label: "Pending", color: "bg-amber-400", icon: Clock };
    }
  };

  const filteredCards = requests.filter(item => {
    const equipName = item.equipment?.equipment_name?.toLowerCase() || "";
    const projName = item.project?.project_name?.toLowerCase() || "";
    const purpose = item.purpose?.toLowerCase() || "";
    const term = search.toLowerCase();

    const matchesSearch = equipName.includes(term) || projName.includes(term) || purpose.includes(term);
    const matchesStatus = statusFilter === 'all' ? true : item.admin_accept.toString() === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const handleOpenDetails = (request) => {
    setSelectedRequest(request);
    setDetailsOpen(true);
  };

  return (
    <div className="md:p-6 bg-white dark:bg-dark-grey min-h-screen">
      <div className="bg-[#F5F1EE] dark:bg-black-primary rounded-2xl border border-[#E8DDD3] dark:border-zinc-800">

        {/* Header Section */}
        <div className="p-4 md:p-6 pb-3 border-b border-[#E8DDD3] dark:border-zinc-800">
          <div className="mb-4 md:mb-6">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Equipment Requests</h1>
            <p className="text-gray-500 dark:text-zinc-400 text-sm mt-1">
              View and track the status of your equipment rental applications.
            </p>
          </div>

          <div className="mt-4 md:mt-8 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search equipment or project..."
                className="pl-10 bg-white dark:bg-zinc-900 border-none shadow-sm h-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="bg-white dark:bg-zinc-900 border-none shadow-sm h-10 w-[160px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="0">Pending</SelectItem>
                <SelectItem value="1">Confirmed</SelectItem>
                <SelectItem value="2">Declined</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Cards Grid */}
        <div className="p-6 pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {isLoading ? (
              <div className="col-span-full text-center py-20 text-gray-500">Loading your requests...</div>
            ) : filteredCards.length > 0 ? (
              filteredCards.map((item) => {
                const status = getStatusInfo(item.admin_accept);
                const StatusIcon = status.icon;

                // Determine which date to show
                const displayDate = item.project?.event_date || item.checkout_date;
                const returnDate = item.expected_return_date;

                return (
                  <div key={item.request_id} className={cardHoverClass}>

                    {/* Status Badge */}
                    <div className="flex justify-between items-center mb-4">
                      <span className={cn(
                        "text-white text-[10px] font-bold px-2.5 py-1.5 rounded-lg flex items-center gap-1.5 uppercase tracking-wider",
                        status.color
                      )}>
                        <StatusIcon className="h-3 w-3" />
                        {status.label}
                      </span>
                    </div>

                    {/* Equipment & Context */}
                    <div className="mb-4">
                      <h3 className="font-bold text-xl leading-tight text-gray-900 dark:text-white mb-2">
                        {item.equipment?.equipment_name}
                      </h3>

                      <div className="flex items-center gap-2 text-sm">
                        {item.project ? (
                          <div className="flex items-center gap-2 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 px-2 py-1 rounded-md">
                            <Briefcase className="h-3.5 w-3.5" />
                            <span className="font-medium truncate max-w-[200px]">{item.project.project_name}</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 px-2 py-1 rounded-md">
                            <User className="h-3.5 w-3.5" />
                            <span className="italic truncate max-w-[200px]">{item.purpose || "Personal Use"}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Date Info */}
                    <div className="flex items-center gap-2 mb-5 text-gray-600 dark:text-zinc-400 text-sm">
                      <CalendarIcon className="h-4 w-4 text-golden" />
                      <span className="font-medium">
                        {displayDate ? new Date(displayDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Date TBD'}
                        {returnDate && ` — ${new Date(returnDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
                      </span>
                    </div>

                    {/* Footer */}
                    <div className="mt-auto pt-4 border-t border-[#F1E9E2] dark:border-zinc-800 flex items-center justify-between">
                      <span className="text-gray-400 dark:text-zinc-500 text-[11px]">
                        Requested On: {new Date(item.created_at).toLocaleDateString()}
                      </span>
                      {/* <Button
                        variant="ghost"
                        className="text-golden hover:text-golden/80 hover:bg-golden/10 p-0 h-auto font-semibold flex gap-1 items-center"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleOpenDetails(item);
                        }}
                      >
                        <Info className="w-4 h-4" /> View Details
                      </Button> */}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="col-span-full text-center py-20 bg-gray-50 dark:bg-zinc-900/50 rounded-xl border-2 border-dashed border-gray-200 dark:border-zinc-800">
                <Package className="h-10 w-10 mx-auto mb-3 opacity-20" />
                <p className="text-gray-500">No requests found.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {selectedRequest && (
        <EquipmentRentDetailsModal
          open={detailsOpen}
          onOpenChange={setDetailsOpen}
          equipment={selectedRequest}
        />
      )}
    </div>
  );
};

export default EquipmentRequestsPage;
